import React from "react";
import Header from "./Header";
import Footer from "./Footer";
import { jsPDF } from "jspdf";
import { Download, FileText } from "lucide-react";

const Results = ({ data }) => {
  if (!data) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-900">
        <Header />
        <main className="flex-grow p-8 flex items-center justify-center">
          <div className="text-center space-y-4">
            <FileText className="w-16 h-16 text-slate-400 mx-auto" />
            <p className="text-xl text-slate-400">
              No data available. Please upload a report.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Ensure data is a string before processing
  const formattedData = (typeof data === "string" ? data : JSON.stringify(data))
    .split("\n")
    .map((line) => line.replace(/[\*]+/g, "").trim())
    .filter((line) => line);

  const downloadPDF = () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4"
    });

    let yPos = 50;
    const margin = 40;
    const pageWidth = doc.internal.pageSize.getWidth();
    
    const mrnLine = formattedData.find(line => line.toLowerCase().includes("mrn:"));
    const mrn = mrnLine ? mrnLine.split(":")[1].trim() : "unknown";

    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("Extracted Medical Information", margin, yPos);
    yPos += 40;

    const patientName = formattedData.find((line) => line.startsWith("Name:"));
    if (patientName) {
      const name = patientName.split(":")[1]?.trim() || "Unknown";
      doc.setFontSize(16);
      doc.text(`Patient Name: ${name}`, margin, yPos);
      yPos += 30;
    }

    doc.setFontSize(12);
    const lineHeight = 20;

    formattedData.forEach((line) => {
      if (yPos > 750) {
        doc.addPage();
        yPos = 50;
      }

      if (line.startsWith("5. Current Diagnoses:")) {
        doc.setFont("helvetica", "bold");
        doc.text("5. Current Diagnoses:", margin, yPos);
        yPos += lineHeight;
        doc.setFont("helvetica", "normal");

        const diagnosesData = line.substring(20).trim();
        const diagnosesList = diagnosesData.split(";");

        diagnosesList.forEach((diagnosis) => {
          if (yPos > 750) {
            doc.addPage();
            yPos = 50;
          }

          const match = diagnosis.match(/(.*?)\((.*?)\)/);
          if (match) {
            const disease = match[1].trim();
            const onsetDate = match[2].trim();
            doc.text(`• ${disease} (Onset: ${onsetDate})`, margin + 20, yPos);
          } else {
            doc.text(`• ${diagnosis.trim()}`, margin + 20, yPos);
          }
          yPos += lineHeight;
        });
      } else {
        const colonIndex = line.indexOf(":");
        if (colonIndex !== -1) {
          const label = line.substring(0, colonIndex).trim();
          const value = line.substring(colonIndex + 1).trim();
          
          doc.setFont("helvetica", "bold");
          const labelWidth = doc.getTextWidth(`${label}: `);
          doc.text(`${label}: `, margin, yPos);
          
          doc.setFont("helvetica", "normal");
          
          const maxWidth = pageWidth - (margin * 2 + labelWidth + 10);
          const valueLines = doc.splitTextToSize(value, maxWidth);
          doc.text(valueLines, margin + labelWidth, yPos);
          
          yPos += lineHeight * valueLines.length;
        } else {
          doc.setFont("helvetica", "normal");
          const textLines = doc.splitTextToSize(line, pageWidth - (margin * 2));
          doc.text(textLines, margin, yPos);
          yPos += lineHeight * textLines.length;
        }
      }
      yPos += 10;
    });

    doc.save(`Medical_Report_${mrn}.pdf`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-900">
      <Header />
      <main className="flex-grow p-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Medical Report
            </h2>
            <button
              onClick={downloadPDF}
              className="flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-3 rounded-lg text-white font-medium shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 transition-all duration-200 hover:scale-105"
            >
              <Download className="w-5 h-5" />
              Download PDF
            </button>
          </div>
  
          {/* Unified Container for All Data */}
          <div className="bg-slate-800/50 backdrop-blur-lg rounded-xl p-8 shadow-lg border border-slate-700/50 hover:border-cyan-500/30 transition-colors duration-300 space-y-6">
            {formattedData.map((line, index) => {
              const colonIndex = line.indexOf(":");
              if (colonIndex !== -1) {
                const label = line.substring(0, colonIndex).trim();
                const value = line.substring(colonIndex + 1).trim();
                return (
                  <div key={index}>
                    <p className="text-sm font-medium text-cyan-400">{label}:</p>
                    <p className="text-lg text-slate-200">{value}</p>
                  </div>
                );
              }
  
              if (line.startsWith("5. Current Diagnoses:")) {
                const diagnosesData = line.substring(20).trim();
                const diagnosesList = diagnosesData.split(";");
  
                return (
                  <div key={index}>
                    <p className="text-sm font-medium text-cyan-400 mb-4">
                      Current Diagnoses
                    </p>
                    <div className="space-y-3">
                      {diagnosesList.map((diagnosis, dIndex) => {
                        const match = diagnosis.match(/(.*?)\((.*?)\)/);
                        if (match) {
                          return (
                            <div
                              key={dIndex}
                              className="flex items-center gap-3 bg-slate-700/30 rounded-lg p-3"
                            >
                              <div className="w-2 h-2 rounded-full bg-cyan-400" />
                              <div>
                                <p className="text-slate-200">{match[1].trim()}</p>
                                <p className="text-sm text-slate-400">
                                  Onset: {match[2].trim()}
                                </p>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div
                            key={dIndex}
                            className="flex items-center gap-3 bg-slate-700/30 rounded-lg p-3"
                          >
                            <div className="w-2 h-2 rounded-full bg-cyan-400" />
                            <p className="text-slate-200">{diagnosis.trim()}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }
  
              return (
                <p key={index} className="text-lg text-slate-200">
                  {line}
                </p>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Results;